import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  IconButton,
  CardActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import AuthorFormModal from '../components/AuthorFormModal';
import CollectionSelect from '../components/CollectionSelect';
import ArtworkTagsEditor from '../components/ArtworkTagsEditor';
import { CURRENCIES } from '../utils/currencies';
import { getArtworksListPath } from '../utils/listViewState';
import axios from 'axios';
import { format, parse } from 'date-fns';

export default function ArtworkForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    collection_code: '',
    author_id: '',
    artwork_title: '',
    artwork_description: '',
    material_and_technique: '',
    height_cm: '',
    width_cm: '',
    depth_cm: '',
    weight_kg: '',
    condition: '',
    condition_details: '',
    source_of_acquisition: '',
    acquisition_date: format(new Date(), 'dd.MM.yyyy'),
    contract_number: '',
    contract_date: '',
    agreed_price: '',
    purchase_currency: 'RUB',
    acquisition_place: '',
    creation_year: new Date().getFullYear(),
    creation_year_text: '',
    has_exact_creation_year: true,
    signatures_and_marks: '',
    photo_reference: '',
    exhibition_notes: '',
    status: 'на хранении',
    sales_price: '',
    sales_currency: 'RUB',
    sales_date: '',
    buyer_name: '',
    storage_place: '',
    buying_collector: '',
    storage_location: '',
    primary_image_id: null,
  });

  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  /** При создании: выбранные файлы до сохранения произведения. Элемент: { file, preview } */
  const [pendingImages, setPendingImages] = useState([]);
  /** Индекс фото в pendingImages, которое пойдёт на карточку (0-based), или null */
  const [primaryPendingIndex, setPrimaryPendingIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [authorModalOpen, setAuthorModalOpen] = useState(false);
  const [authorModalEdit, setAuthorModalEdit] = useState(null);
  /** Инициализируем форму из artwork только при первом загрузке экспоната, чтобы refetch после загрузки картинки не сбрасывал введённые данные */
  const initializedArtworkIdRef = useRef(null);

  useEffect(() => {
    initializedArtworkIdRef.current = null;
  }, [id]);

  const { data: artwork, isLoading: loadingArtwork } = useQuery(
    ['artwork', id],
    async () => {
      const response = await axios.get(`/api/artworks/${id}`);
      return response.data;
    },
    { enabled: isEdit }
  );

  useEffect(() => {
    if (artwork && artwork.images) {
      setImages(artwork.images || []);
    }
  }, [artwork]);

  const { data: authors } = useQuery('authors', async () => {
    const response = await axios.get('/api/authors');
    return response.data;
  });

  useEffect(() => {
    if (artwork && isEdit && initializedArtworkIdRef.current !== artwork.id) {
      initializedArtworkIdRef.current = artwork.id;
      setFormData({
        collection_code: artwork.collection_code || '',
        author_id: artwork.author_id || '',
        artwork_title: artwork.artwork_title || '',
        artwork_description: artwork.artwork_description || '',
        material_and_technique: artwork.material_and_technique || '',
        height_cm: artwork.height_cm || '',
        width_cm: artwork.width_cm || '',
        depth_cm: artwork.depth_cm || '',
        weight_kg: artwork.weight_kg || '',
        condition: artwork.condition || '',
        condition_details: artwork.condition_details || '',
        source_of_acquisition: artwork.source_of_acquisition || '',
        acquisition_date: artwork.acquisition_date || format(new Date(), 'dd.MM.yyyy'),
        contract_number: artwork.contract_number || '',
        contract_date: artwork.contract_date || '',
        agreed_price: artwork.agreed_price || '',
        purchase_currency: artwork.purchase_currency || 'RUB',
        acquisition_place: artwork.acquisition_place || '',
        creation_year:
          artwork.creation_year != null
            ? artwork.creation_year
            : new Date().getFullYear(),
        creation_year_text: artwork.creation_year_text || '',
        has_exact_creation_year:
          artwork.has_exact_creation_year !== false && artwork.creation_year != null,
        signatures_and_marks: artwork.signatures_and_marks || '',
        photo_reference: artwork.photo_reference || '',
        exhibition_notes: artwork.exhibition_notes || '',
        status: artwork.status || 'на хранении',
        sales_price: artwork.sales_price || '',
        sales_currency: artwork.sales_currency || 'RUB',
        sales_date: artwork.sales_date || '',
        buyer_name: artwork.buyer_name || '',
        storage_place: artwork.storage_place || '',
        buying_collector: artwork.buying_collector || '',
        storage_location: artwork.storage_location || '',
        primary_image_id: artwork.primary_image_id || null,
      });
    }
  }, [artwork, isEdit]);

  const mutation = useMutation(
    async (data) => {
      if (isEdit) {
        return axios.put(`/api/artworks/${id}`, data);
      } else {
        return axios.post('/api/artworks', data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('artworks');
        navigate(getArtworksListPath());
      },
    }
  );

  const prepareSubmitData = () => {
    const submitData = { ...formData };
    if (!isEdit) delete submitData.primary_image_id;
    else if (submitData.primary_image_id === '') submitData.primary_image_id = null;

    const numericFields = ['height_cm', 'width_cm', 'depth_cm', 'weight_kg', 'agreed_price', 'sales_price'];
    numericFields.forEach(field => {
      if (submitData[field] === '' || submitData[field] === null || submitData[field] === undefined) {
        submitData[field] = null;
      } else {
        const parsed = parseFloat(submitData[field]);
        submitData[field] = isNaN(parsed) ? null : parsed;
      }
    });
    if (submitData.author_id) submitData.author_id = parseInt(submitData.author_id);

    const exact = submitData.has_exact_creation_year !== false;
    submitData.has_exact_creation_year = exact;
    if (exact) {
      const year = parseInt(submitData.creation_year, 10);
      submitData.creation_year = Number.isFinite(year) ? year : null;
      submitData.creation_year_text = null;
    } else {
      submitData.creation_year = null;
      const text = (submitData.creation_year_text || '').trim();
      submitData.creation_year_text = text || null;
    }
    return submitData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = prepareSubmitData();

    if (isEdit) {
      mutation.mutate(submitData);
      return;
    }

    if (pendingImages.length > 0) {
      setSubmitting(true);
      setUploadError(null);
      try {
        const createRes = await axios.post('/api/artworks', submitData);
        const newId = createRes.data.id;
        const uploadedIds = [];
        for (let i = 0; i < pendingImages.length; i++) {
          const fd = new FormData();
          fd.append('file', pendingImages[i].file);
          const imgRes = await axios.post(`/api/artworks/${newId}/images`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          uploadedIds.push(imgRes.data.id);
        }
        if (primaryPendingIndex != null && uploadedIds[primaryPendingIndex] != null) {
          await axios.put(`/api/artworks/${newId}`, {
            primary_image_id: uploadedIds[primaryPendingIndex],
          });
        }
        queryClient.invalidateQueries('artworks');
        navigate(getArtworksListPath());
      } catch (err) {
        setUploadError(err.response?.data?.error || 'Ошибка сохранения или загрузки фото');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    mutation.mutate(submitData);
  };

  const handlePendingImagesSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (pendingImages.length + files.length > 10) {
      setUploadError(`Можно загрузить максимум 10 изображений. Уже выбрано: ${pendingImages.length}`);
      return;
    }
    setUploadError(null);
    const newEntries = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPendingImages((prev) => [...prev, ...newEntries]);
    if (primaryPendingIndex === null && pendingImages.length === 0) setPrimaryPendingIndex(0);
    e.target.value = '';
  };

  const removePendingImage = (index) => {
    setPendingImages((prev) => {
      const next = prev.filter((_, i) => i !== index);
      prev[index]?.preview && URL.revokeObjectURL(prev[index].preview);
      return next;
    });
    setPrimaryPendingIndex((prev) => {
      if (prev === null) return null;
      if (prev === index) return pendingImages.length > 1 ? 0 : null;
      if (prev > index) return prev - 1;
      return prev;
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Проверка количества изображений
    if (images.length + files.length > 10) {
      setUploadError(`Можно загрузить максимум 10 изображений. Уже загружено: ${images.length}`);
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`/api/artworks/${id}/images`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      });

      const uploadedImages = await Promise.all(uploadPromises);
      setImages([...images, ...uploadedImages]);
      queryClient.invalidateQueries(['artwork', id]);
    } catch (error) {
      setUploadError(error.response?.data?.error || 'Ошибка загрузки изображений');
    } finally {
      setUploading(false);
      e.target.value = ''; // Сброс input
    }
  };

  const handleImageDelete = async (imageId) => {
    try {
      await axios.delete(`/api/artworks/${id}/images/${imageId}`);
      setImages(images.filter(img => img.id !== imageId));
      queryClient.invalidateQueries(['artwork', id]);
    } catch (error) {
      setUploadError(error.response?.data?.error || 'Ошибка удаления изображения');
    }
  };

  const openCreateAuthor = () => {
    setAuthorModalEdit(null);
    setAuthorModalOpen(true);
  };

  const openEditAuthor = () => {
    const selected = authors?.find((a) => a.id === parseInt(formData.author_id, 10));
    if (selected) {
      setAuthorModalEdit(selected);
      setAuthorModalOpen(true);
    }
  };

  const closeAuthorModal = () => {
    setAuthorModalOpen(false);
    setAuthorModalEdit(null);
  };

  const onAuthorSuccess = (savedAuthor) => {
    queryClient.invalidateQueries('authors');
    if (!authorModalEdit) {
      setFormData((prev) => ({ ...prev, author_id: savedAuthor.id }));
    }
  };

  if (loadingArtwork && isEdit) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Редактирование произведения' : 'Новое произведение'}
      </Typography>

      {isEdit && artwork?.id && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Теги
          </Typography>
          <ArtworkTagsEditor artworkId={artwork.id} tags={artwork.tags} editable />
        </Box>
      )}

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mutation.error?.response?.data?.error || 'Ошибка сохранения'}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <CollectionSelect
                required
                value={formData.collection_code}
                onChange={(e) =>
                  setFormData({ ...formData, collection_code: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="flex-start" gap={1} sx={{ width: '100%' }}>
                <FormControl required sx={{ flex: 1 }} size="small">
                  <InputLabel>Автор</InputLabel>
                  <Select
                    value={formData.author_id}
                    label="Автор"
                    onChange={(e) =>
                      setFormData({ ...formData, author_id: e.target.value })
                    }
                  >
                    {authors?.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.full_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  color="primary"
                  onClick={openCreateAuthor}
                  title="Добавить автора"
                  size="small"
                  sx={{ mt: 0.5 }}
                >
                  <PersonAddIcon />
                </IconButton>
                <IconButton
                  color="primary"
                  onClick={openEditAuthor}
                  disabled={!formData.author_id}
                  title="Редактировать автора"
                  size="small"
                  sx={{ mt: 0.5 }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Название произведения"
                value={formData.artwork_title}
                onChange={(e) =>
                  setFormData({ ...formData, artwork_title: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={4}
                label="Описание"
                value={formData.artwork_description}
                onChange={(e) =>
                  setFormData({ ...formData, artwork_description: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Материалы и техника"
                value={formData.material_and_technique}
                onChange={(e) =>
                  setFormData({ ...formData, material_and_technique: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {formData.has_exact_creation_year ? (
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Год создания"
                    value={formData.creation_year}
                    onChange={(e) =>
                      setFormData({ ...formData, creation_year: e.target.value })
                    }
                  />
                ) : (
                  <TextField
                    fullWidth
                    required
                    label="Год создания (текст)"
                    placeholder="Например: начало XX века, ок. 1950"
                    value={formData.creation_year_text}
                    onChange={(e) =>
                      setFormData({ ...formData, creation_year_text: e.target.value })
                    }
                  />
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!formData.has_exact_creation_year}
                      onChange={(e) => {
                        const noExact = e.target.checked;
                        setFormData({
                          ...formData,
                          has_exact_creation_year: !noExact,
                          ...(noExact
                            ? {
                                creation_year_text:
                                  formData.creation_year_text ||
                                  (formData.creation_year
                                    ? String(formData.creation_year)
                                    : ''),
                              }
                            : {
                                creation_year:
                                  formData.creation_year ||
                                  new Date().getFullYear(),
                              }),
                        });
                      }}
                    />
                  }
                  label="Точной даты нет"
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Высота (см)"
                value={formData.height_cm}
                onChange={(e) =>
                  setFormData({ ...formData, height_cm: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Ширина (см)"
                value={formData.width_cm}
                onChange={(e) =>
                  setFormData({ ...formData, width_cm: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Глубина (см)"
                value={formData.depth_cm}
                onChange={(e) =>
                  setFormData({ ...formData, depth_cm: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Вес (кг)"
                value={formData.weight_kg}
                onChange={(e) =>
                  setFormData({ ...formData, weight_kg: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Состояние сохранности</InputLabel>
                <Select
                  value={formData.condition}
                  label="Состояние сохранности"
                  onChange={(e) =>
                    setFormData({ ...formData, condition: e.target.value })
                  }
                >
                  <MenuItem value="хорошее">Хорошее</MenuItem>
                  <MenuItem value="удовлетворительное">Удовлетворительное</MenuItem>
                  <MenuItem value="требует реставрации">Требует реставрации</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Источник поступления"
                value={formData.source_of_acquisition}
                onChange={(e) =>
                  setFormData({ ...formData, source_of_acquisition: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Дата поступления"
                value={formData.acquisition_date}
                onChange={(e) =>
                  setFormData({ ...formData, acquisition_date: e.target.value })
                }
                placeholder="ДД.ММ.ГГГГ"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Стоимость покупки"
                value={formData.agreed_price}
                onChange={(e) =>
                  setFormData({ ...formData, agreed_price: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Валюта покупки</InputLabel>
                <Select
                  value={formData.purchase_currency}
                  label="Валюта покупки"
                  onChange={(e) =>
                    setFormData({ ...formData, purchase_currency: e.target.value })
                  }
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Место приобретения"
                value={formData.acquisition_place}
                onChange={(e) =>
                  setFormData({ ...formData, acquisition_place: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Фактическая стоимость продажи"
                value={formData.sales_price}
                onChange={(e) =>
                  setFormData({ ...formData, sales_price: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Валюта продажи</InputLabel>
                <Select
                  value={formData.sales_currency}
                  label="Валюта продажи"
                  onChange={(e) =>
                    setFormData({ ...formData, sales_currency: e.target.value })
                  }
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Дата продажи"
                value={formData.sales_date}
                onChange={(e) =>
                  setFormData({ ...formData, sales_date: e.target.value })
                }
                placeholder="ДД.ММ.ГГГГ"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Покупатель"
                value={formData.buyer_name}
                onChange={(e) =>
                  setFormData({ ...formData, buyer_name: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Статус</InputLabel>
                <Select
                  value={formData.status}
                  label="Статус"
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  <MenuItem value="на хранении">На хранении</MenuItem>
                  <MenuItem value="в экспозиции">В экспозиции</MenuItem>
                  <MenuItem value="на реставрации">На реставрации</MenuItem>
                  <MenuItem value="потерянно">Потерянно</MenuItem>
                  <MenuItem value="продано">Продано</MenuItem>
                  <MenuItem value="возвращено">Возвращено</MenuItem>
                  <MenuItem value="исключено">Исключено</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Место создания"
                value={formData.storage_place}
                onChange={(e) =>
                  setFormData({ ...formData, storage_place: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Купивший коллекционер"
                value={formData.buying_collector}
                onChange={(e) =>
                  setFormData({ ...formData, buying_collector: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Локация хранения"
                value={formData.storage_location}
                onChange={(e) =>
                  setFormData({ ...formData, storage_location: e.target.value })
                }
              />
            </Grid>

            {!isEdit && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Фото произведения ({pendingImages.length}/10)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Загрузите фото и отметьте то, которое будет отображаться на карточке в списке.
                  </Typography>
                  {uploadError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
                      {uploadError}
                    </Alert>
                  )}
                  <Box sx={{ mb: 2 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="pending-image-upload"
                      type="file"
                      multiple
                      onChange={handlePendingImagesSelect}
                      disabled={pendingImages.length >= 10}
                    />
                    <label htmlFor="pending-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        disabled={pendingImages.length >= 10}
                        sx={{ mb: 2 }}
                      >
                        Выбрать фото
                      </Button>
                    </label>
                    {pendingImages.length >= 10 && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 2, display: 'inline' }}>
                        Максимум 10 изображений
                      </Typography>
                    )}
                  </Box>
                  {pendingImages.length > 0 && (
                    <Grid container spacing={2}>
                      {pendingImages.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Card>
                            <CardMedia
                              component="img"
                              height="200"
                              image={item.preview}
                              alt={item.file.name}
                              sx={{ objectFit: 'contain' }}
                            />
                            <CardActions sx={{ flexWrap: 'wrap' }}>
                              <Typography variant="caption" sx={{ flexGrow: 1, px: 1 }} noWrap>
                                {item.file.name}
                              </Typography>
                              <IconButton
                                size="small"
                                color={primaryPendingIndex === index ? 'primary' : 'default'}
                                onClick={() =>
                                  setPrimaryPendingIndex((prev) =>
                                    prev === index ? null : index
                                  )
                                }
                                title={primaryPendingIndex === index ? 'Убрать с карточки' : 'Показать на карточке'}
                              >
                                {primaryPendingIndex === index ? <StarIcon /> : <StarBorderIcon />}
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removePendingImage(index)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </CardActions>
                            {primaryPendingIndex === index && (
                              <Typography variant="caption" color="primary" sx={{ px: 1, pb: 0.5 }}>
                                На карточке
                              </Typography>
                            )}
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Paper>
              </Grid>
            )}

            {isEdit && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Изображения произведения ({images.length}/10)
                  </Typography>
                  
                  {uploadError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
                      {uploadError}
                    </Alert>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="image-upload"
                      type="file"
                      multiple
                      onChange={handleImageUpload}
                      disabled={uploading || images.length >= 10}
                    />
                    <label htmlFor="image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        disabled={uploading || images.length >= 10}
                        sx={{ mb: 2 }}
                      >
                        {uploading ? 'Загрузка...' : 'Загрузить изображения'}
                      </Button>
                    </label>
                    {images.length >= 10 && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 2, display: 'inline' }}>
                        Достигнуто максимальное количество изображений
                      </Typography>
                    )}
                  </Box>

                  {images.length > 0 && (
                    <Grid container spacing={2}>
                      {images.map((image) => (
                        <Grid item xs={12} sm={6} md={4} key={image.id}>
                          <Card>
                            <CardMedia
                              component="img"
                              height="200"
                              image={`/uploads/${image.file_path}`}
                              alt={image.file_name}
                              sx={{ objectFit: 'contain' }}
                            />
                            <CardActions sx={{ flexWrap: 'wrap' }}>
                              <Typography variant="caption" sx={{ flexGrow: 1, px: 1 }}>
                                {image.file_name}
                              </Typography>
                              <IconButton
                                size="small"
                                color={formData.primary_image_id === image.id ? 'primary' : 'default'}
                                onClick={() =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    primary_image_id: prev.primary_image_id === image.id ? null : image.id,
                                  }))
                                }
                                title={formData.primary_image_id === image.id ? 'Убрать с карточки' : 'Показать в карточке'}
                              >
                                {formData.primary_image_id === image.id ? <StarIcon /> : <StarBorderIcon />}
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleImageDelete(image.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </CardActions>
                            {formData.primary_image_id === image.id && (
                              <Typography variant="caption" color="primary" sx={{ px: 1, pb: 0.5 }}>
                                Отображается в карточке
                              </Typography>
                            )}
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button onClick={() => navigate(getArtworksListPath())} disabled={mutation.isLoading || submitting}>
                  Отмена
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={mutation.isLoading || submitting}
                >
                  {(mutation.isLoading || submitting) ? <CircularProgress size={24} /> : 'Сохранить'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <AuthorFormModal
        open={authorModalOpen}
        onClose={closeAuthorModal}
        author={authorModalEdit}
        onSuccess={onAuthorSuccess}
      />
    </Box>
  );
}

